import React, { useMemo } from "react";
import { useLocation } from "react-router-dom";

interface SearchHighlighterProps {
  children: React.ReactNode;
}

/**
 * SearchHighlighter component that scans its children for a search term 
 * provided in the URL (?h=query) and wraps matches in a highlighted span.
 */
const SearchHighlighter: React.FC<SearchHighlighterProps> = ({ children }) => {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const highlightQuery = searchParams.get("h");

  const highlightText = (node: React.ReactNode): React.ReactNode => {
    if (!highlightQuery || !highlightQuery.trim()) return node;

    if (typeof node === "string") {
      const tokens = highlightQuery.toLowerCase().split(/\s+/).filter(t => t.length > 0);
      if (tokens.length === 0) return node;

      // Create a regex that matches the exact phrase if possible, or individual tokens
      // We prioritize the exact phrase first
      const escapedQuery = highlightQuery.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery}|${tokens.join("|")})`, "gi");
      
      const parts = node.split(regex);
      if (parts.length === 1) return node;

      return (
        <>
          {parts.map((part, i) => 
            regex.test(part) ? (
              <span 
                key={i} 
                className="bg-yellow-200 text-black px-0.5 rounded-sm font-medium animate-pulse-subtle"
                style={{ 
                  boxShadow: '0 0 8px rgba(253, 224, 71, 0.5)',
                  transition: 'all 0.3s ease'
                }}
              >
                {part}
              </span>
            ) : (
              part
            )
          )}
        </>
      );
    }

    if (React.isValidElement(node)) {
      // Don't recurse into certain elements like scripts, styles, or interactive buttons maybe?
      // For now, recurse into all standard HTML/React elements
      return React.cloneElement(node, {
        children: React.Children.map(node.props.children, highlightText),
      } as any);
    }

    if (Array.isArray(node)) {
      return node.map((child, index) => <React.Fragment key={index}>{highlightText(child)}</React.Fragment>);
    }

    return node;
  };

  return <>{useMemo(() => highlightText(children), [children, highlightQuery])}</>;
};

export default SearchHighlighter;
